import { useEffect, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';

import { IEcpStore } from '../../../../../../rest/ecp-locator';
import {
  sortingType,
  pinnedStoreCode,
  setMarkerCode,
} from '../../../../../../graphql/ecp-locator/ecp-locator.cache';
import { SortingTypes } from '../../ecp-sort/ecp-sort.models';
import { sortByRaiting, sortByDistance } from '../../../../../../utils/ecp-locator';

const normalizeForMatch = (str: string): string =>
  str.toLowerCase().replace(/[^a-zа-яё0-9]/g, '');

const findMatchedStore = (stores: IEcpStore[], address: string): IEcpStore | undefined => {
  const normalized = normalizeForMatch(address);

  return stores.find((store) => {
    const storeNormalized = normalizeForMatch(store.street);
    return (
      storeNormalized === normalized ||
      storeNormalized.includes(normalized) ||
      normalized.includes(storeNormalized)
    );
  });
};

export const useSetStoreByAddress = (stores: IEcpStore[] | undefined): IEcpStore[] => {
  const currentSortingType = useReactiveVar(sortingType);
  const currentPinnedCode = useReactiveVar(pinnedStoreCode);
  const [searchParams] = useSearchParams();
  const urlAddress = searchParams.get('address');
  const [orderedStores, setOrderedStores] = useState<IEcpStore[]>([]);

  useEffect(() => {
    if (urlAddress) {
      const matchedStore = findMatchedStore(stores || [], urlAddress);
      if (matchedStore && matchedStore.code !== currentPinnedCode) {
        pinnedStoreCode(matchedStore.code);
        setMarkerCode(matchedStore.code);
      }
    }
  }, [stores, urlAddress, currentPinnedCode]);

  useEffect(() => {
    if (!stores) {
      setOrderedStores([]);
      return;
    }

    const isSortByRating = currentSortingType === SortingTypes.rating;
    const sortingCallback = isSortByRating ? sortByRaiting : sortByDistance;
    let sorted = sortingCallback([...stores]);

    if (currentPinnedCode) {
      const pinnedIndex = sorted.findIndex(({ code }: IEcpStore) => code === currentPinnedCode);
      if (pinnedIndex > 0) {
        const [pinned] = sorted.splice(pinnedIndex, 1);
        sorted = [pinned, ...sorted];
      }
    }

    setOrderedStores((prev) => {
      const isSameOrder =
        prev.length === sorted.length &&
        sorted.every((store, i) => store.code === prev[i].code);
      return isSameOrder ? prev : sorted;
    });
  }, [stores, currentSortingType, currentPinnedCode]);

  return orderedStores;
};
